import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CafDashboardWindow, CafDecisionRow, CafReplacementItem, CafReplacementReport, getCafReplacementReport } from '../../../api/cafCorpus';
import { FINAL_RESPONSE_CHECK_LABEL } from '../copy';
import { formatDayTime } from '../format';
import { senderEmail } from '../lenders';
import { DecisionDrawer } from './DecisionDrawer';
import { OneEmailModal } from '../teach/OneEmailModal';
import { DashboardSession } from '../../../context/DashboardSessionContext';

// One label per stored reason code: each button filters that exact code.
// Gate labels follow caf_automation_gate's named checks; classifier labels
// follow its effective hold reasons. Unknown future codes get stable numbers
// within this frozen report cohort, never an indistinguishable shared label.
const REASON_NAMES: Record<string, string> = {
  current_final_response: 'Final response',
  final_response_uncertain: FINAL_RESPONSE_CHECK_LABEL,
  final_response_example_conflict: 'Examples said not a final response, but the email says it is',
  final_response_example_unreadable: "Examples said not a final response, but part of the letter couldn't be read",
  claim_match_absent: 'No claim match found',
  claim_match_failed: 'Claim lookup could not finish',
  claim_match_ambiguous: 'More than one claim matched',
  claim_reference_conflict: 'Claim references conflict',
  rag_identity_ambiguous: 'Identity details are ambiguous',
  rag_identity_unverified: 'Identity details could not be verified',
  rag_guidance_not_supplied: 'Approved guidance was not available to the AI',
  rag_no_suitable_match: 'No suitable approved example found',
  cancelled_claim_review: 'Cancelled claim needs staff review',
  cancellation_state_disagreement: 'Claim cancellation status differs',
  password_protected: 'Password-protected attachment',
  attachment_requires_review: 'Attachment needs staff review',
  rag_model_unavailable: 'AI model unavailable',
  rag_technical_failure: 'AI processing failed',
  rag_unreadable: 'AI answer unreadable',
  rag_invalid_model_output: 'AI answer format could not be used',
  duplicate_complaint: 'Possible duplicate complaint',
  no_hold: 'An AI hold blocks automation',
  followed: 'No approved example was followed',
  unanimous: 'Approved examples disagree',
  agrees: 'AI and approved example disagree',
  claim: 'Claim check needs staff review',
  lender_mail: 'Email is outside the lender lane',
  backstops: 'Workflow safety check needs review',
  linked: 'Linked workflow is not ready',
  workflow_matches: "Workflow doesn't match the example",
  first_decision: 'Task already has a committed decision',
  no_duplicate: 'Workflow already ran recently for this claim',
  final_response_hold: 'Final response held for staff',
  unknown: 'Reason not recorded',
  unavailable: 'Reason data unavailable',
};

const reasonName = (code: string, codes: string[]): string => {
  if (REASON_NAMES[code]) return REASON_NAMES[code];
  const otherCodes = Array.from(new Set(codes.filter((candidate) => !REASON_NAMES[candidate]))).sort();
  const index = otherCodes.indexOf(code);
  return `Other reason ${index >= 0 ? index + 1 : otherCodes.length + 1}`;
};

const UTC_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatUtc = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Time unavailable';
  const pad = (number: number) => String(number).padStart(2, '0');
  return `${date.getUTCDate()} ${UTC_MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}, ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())} UTC`;
};

const asDecisionRow = (item: CafReplacementItem): CafDecisionRow => ({
  id: item.task_id, decision_id: item.decision_id, email_from: item.email_from,
  subject: item.subject, processed_at: item.decided_at, action: item.outcome === 'manual' ? 'MANUAL_REVIEW' : null,
  category: null, confidence: null, reasoning: null, claim_reference: null,
  assigned_claim_id: null, completed: false, completed_by: null, precedents: [], flags: [],
  hold_reason: item.reason_code,
});

interface Props {
  days: CafDashboardWindow;
  refreshKey: number;
  lenderOf: (sender: string) => string | null;
  onLabelSender: (sender: string) => void;
  onShowExample: (entryId: string, sender: string) => void;
  session?: DashboardSession;
  canAutomate?: boolean;
}

export const WatchingReport: React.FC<Props> = ({ days, refreshKey, lenderOf, onLabelSender, onShowExample, session, canAutomate = false }) => {
  const [report, setReport] = useState<CafReplacementReport | null>(null);
  const [loadedOwner, setLoadedOwner] = useState<DashboardSession | undefined>();
  const [items, setItems] = useState<CafReplacementItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [cohort, setCohort] = useState<string | undefined>();
  const [outcome, setOutcome] = useState<'workflow' | 'manual' | undefined>();
  const [reason, setReason] = useState<string | undefined>();
  const [open, setOpen] = useState<CafReplacementItem | null>(null);
  const [reviewRefreshKey, setReviewRefreshKey] = useState(0);
  const [teach, setTeach] = useState<{ row: CafDecisionRow; correction?: { decisionId: string; reviewRevision: number; feedbackOnly?: boolean } } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const generation = useRef(0);
  const controller = useRef<AbortController | null>(null);
  const [retry, setRetry] = useState(0);
  const visibleReport = loadedOwner === session ? report : null;
  const effectiveCohort = loadedOwner === session ? cohort : undefined;

  const load = useCallback(async (after?: string) => {
    const id = ++generation.current;
    controller.current?.abort();
    const next = new AbortController();
    controller.current = next;
    setLoading(true);
    setError(false);
    if (!after) setItems([]);
    try {
      const response = await getCafReplacementReport({ days, limit: 50, outcome, reason,
        ...(after ? { cursor: after } : effectiveCohort ? { cohort: effectiveCohort } : {}) }, next.signal,
        session?.acceptsAuthorization);
      if (id !== generation.current || (session && (!session.active || session.isCurrent?.() === false))) return;
      setReport(response);
      setLoadedOwner(session);
      setItems((previous) => after ? [...previous, ...(response.items ?? [])] : response.items ?? []);
      setCursor(response.next_cursor);
      if (response.cohort) setCohort(response.cohort);
    } catch {
      if (!next.signal.aborted && id === generation.current && (!session || (session.active && session.isCurrent?.() !== false))) setError(true);
    } finally {
      if (id === generation.current) setLoading(false);
    }
  }, [days, outcome, reason, effectiveCohort, session]);

  useEffect(() => {
    void load();
    return () => { generation.current += 1; controller.current?.abort(); };
  // Cohort is deliberately retained when a filter changes. A new day or explicit refresh gets a fresh cohort.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, outcome, reason, refreshKey, retry, session]);

  const chooseReason = (code: string) => { setReason(code); setOutcome('manual'); };
  const clearFilter = () => { setReason(undefined); setOutcome(undefined); };
  const retryReport = () => { setCohort(undefined); setRetry((value) => value + 1); };
  const selectedRow = open ? asDecisionRow(open) : null;
  const reasonCodes = visibleReport?.manual_reasons?.map((entry) => entry.code) ?? [];

  return <section className="ui-card cur-span-12" aria-label="Watching results">
    <div className="ui-card__header"><h2 className="ui-card__title">Watching results</h2></div>
    <div className="ui-card__body">
      <p className="cur-hint">AI proposals · counted by decision · {visibleReport
        ? `From ${formatUtc(visibleReport.window_start)} to before ${formatUtc(visibleReport.window_end_exclusive)} · as of ${formatUtc(visibleReport.as_of)}`
        : `last ${days} UTC days`}. No action was taken by these proposals.</p>
      {loading && !visibleReport && <p role="status">Loading Watching report…</p>}
      {error && <div role="alert">Couldn&apos;t load the Watching report. <button type="button" onClick={retryReport}>Retry Watching report</button></div>}
      {visibleReport?.status === 'not_ready' && <div role="status">Watching report is not ready. <button type="button" onClick={retryReport}>Retry Watching report</button></div>}
      {visibleReport?.status === 'ready' && visibleReport.counts && <>
        <p><strong>{visibleReport.counts.total} decisions</strong> · {visibleReport.counts.would_run} would run · {visibleReport.counts.needs_manual} need a person</p>
        <h3 className="cur-sec-title">Why a person is needed</h3>
        {visibleReport.reasons_status === 'unavailable' ? <p>Reason data is unavailable. Try Refresh later.</p>
          : <>{visibleReport.reasons_status === 'partial' && <p>Some decision reasons are unavailable.</p>}
            {visibleReport.manual_reasons?.length ? <div className="cur-chips">{visibleReport.manual_reasons.map((entry) =>
              <button key={entry.code} type="button" className="cur-chip" aria-pressed={reason === entry.code} onClick={() => chooseReason(entry.code)}>
                {reasonName(entry.code, reasonCodes)}: {entry.count}
              </button>)}</div> : <p>No decisions needed a person in this period.</p>}</>}
        <h3 className="cur-sec-title">Recent watched decisions</h3>
        <div className="cur-chips">
          <button type="button" className="cur-chip" aria-pressed={!outcome && !reason} onClick={clearFilter}>All</button>
          <button type="button" className="cur-chip" aria-pressed={outcome === 'workflow'} onClick={() => { setReason(undefined); setOutcome('workflow'); }}>Would run</button>
          <button type="button" className="cur-chip" aria-pressed={outcome === 'manual' && !reason} onClick={() => { setReason(undefined); setOutcome('manual'); }}>Needs a person</button>
          {reason && <button type="button" className="cur-chip" onClick={clearFilter}>Clear reason filter</button>}
        </div>
        {items.length ? <table className="cur-tbl" aria-label="Recent watched decisions">
          <thead><tr><th>Decided</th><th>Email</th><th>Proposal</th><th>Reason</th></tr></thead>
          <tbody>{items.map((item) => <tr key={item.decision_id} className="cur-row-click" tabIndex={0}
            onClick={() => setOpen(item)} onKeyDown={(event) => { if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); setOpen(item); } }}>
            <td className="cur-nowrap cur-tnum">{formatDayTime(item.decided_at)}</td>
            <td><span>{item.subject || '(No subject)'}</span><br />{lenderOf(item.email_from || '') ?? senderEmail(item.email_from || '')}</td>
            <td>{item.label}</td><td>{item.reason_status === 'unavailable' ? 'Unavailable' : item.reason_code ? reasonName(item.reason_code, reasonCodes) : '—'}</td>
          </tr>)}</tbody>
        </table> : !loading && <p>No watched decisions match this selection.</p>}
        {cursor && <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm" disabled={loading} onClick={() => { void load(cursor); }}>Show more watched decisions</button>}
        {visibleReport.daily && <details>
          <summary className="cur-linkish">Daily decision counts</summary>
          <table className="cur-tbl" aria-label="Watching decisions by UTC day">
            <thead><tr><th>UTC day</th><th>Would run</th><th>Needs a person</th></tr></thead>
            <tbody>{visibleReport.daily.map((day) => <tr key={day.date}><td>{day.date}</td><td>{day.would_run}</td><td>{day.needs_manual}</td></tr>)}</tbody>
          </table>
        </details>}
      </>}
    </div>
    {selectedRow && open && <DecisionDrawer key={open.decision_id} row={selectedRow} watched={open} lenderOf={lenderOf} reviewRefreshKey={reviewRefreshKey}
      onClose={() => setOpen(null)} onChanged={retryReport} onLabelSender={onLabelSender} onTeach={(row, correction) => setTeach({ row, correction })} />}
    {teach && <OneEmailModal senders={[]} preset={{ taskId: teach.row.id, sender: teach.row.email_from, subject: teach.row.subject }} correction={teach.correction} canAutomate={canAutomate}
      onClose={() => setTeach(null)} onSaved={() => { setReviewRefreshKey((key) => key + 1); retryReport(); }}
      onShowExample={(id, sender) => { setTeach(null); setOpen(null); onShowExample(id, sender); }} />}
  </section>;
};
